import { FC, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { Box, Button, Text, useToast, VStack } from '@bonusly/design-system';
import { useLocalStorage } from '@bonusly/storage';
import { useCurrentUser, useIsBizyEarlyAccessEligible } from '@bonusly/who-am-i';

import { useCreateEarlyAccessRequest } from '../../queries/bizy-early-access';
import { Bizy } from '../bizy';

const SOURCE = 'side_panel';

// Scope the confirmed flag by user so a shared browser / account switch doesn't
// show one user another user's "you're on the list" state. useLocalStorage
// re-reads when this key changes, so switching users re-derives the flag.
function confirmedStorageKey(userId: string | undefined): string {
	return `bizy-early-access-requested:${userId ?? 'anon'}`;
}

/**
 * Bizy early-access opt-in CTA, rendered in the nav's bottom-left slot where
 * "Ask Bizy" would otherwise live, for Org-plan customers not yet in the Bizy
 * program (BIZY-322).
 *
 * Clicking records the user's interest (idempotent on the backend) and flips to
 * a confirmed "we'll be in touch" state. It does NOT enable Bizy — the Bonusly
 * team grants access manually after reviewing the collected list.
 */
export const BizyEarlyAccessCard: FC = () => {
	const { t } = useTranslation('vertical_navbar');
	const toast = useToast();
	const isEligible = useIsBizyEarlyAccessEligible();
	const currentUser = useCurrentUser();
	const userId = currentUser?.id;
	// Per-user confirmed flag. useLocalStorage re-reads on key (user) change, so
	// a prior user's "you're on the list" state can't linger after a switch.
	const [confirmed, setConfirmed] = useLocalStorage<boolean>(confirmedStorageKey(userId), false);
	const createEarlyAccessRequest = useCreateEarlyAccessRequest();

	// Track the currently-active user so an in-flight request's onSuccess can
	// tell whether the account changed mid-request.
	const activeUserIdRef = useRef(userId);
	activeUserIdRef.current = userId;

	if (!isEligible) return null;

	const handleClick = () => {
		const requestUserId = userId;
		createEarlyAccessRequest.mutate(
			{ source: SOURCE },
			{
				onSuccess: () => {
					// Only persist + flip if the same user is still active — otherwise
					// setConfirmed would write the confirmed flag under whichever user's
					// key is now current, showing the confirmed state to someone who
					// never opted in. (The backend opt-in already succeeded; a switched-
					// away user re-deriving the CTA just re-POSTs idempotently.)
					if (activeUserIdRef.current === requestUserId) {
						setConfirmed(true);
					}
				},
				onError: () => {
					toast({
						status: 'error',
						description: t('early_access.error', 'Something went wrong. Please try again.'),
					});
				},
			}
		);
	};

	return (
		<Box pt={3} data-testid="bizy-early-access-card">
			<Box bg="secondary.bgAlpha" borderRadius="md" p={3}>
				<VStack align="stretch" spacing={2}>
					<Box display="flex" justifyContent="flex-start">
						<Bizy state={confirmed ? 'excited' : 'curious'} size={40} freeze={confirmed} />
					</Box>

					{confirmed ? (
						<>
							<Text fontSize="sm" fontWeight="medium">
								{t('early_access.confirmed_title', "You're on the list!")}
							</Text>
							<Text fontSize="xs" color="text.soft">
								{t('early_access.confirmed_body', "We'll be in touch when Bizy is ready for your team.")}
							</Text>
						</>
					) : (
						<>
							<Text fontSize="sm" fontWeight="medium">
								{t('early_access.cta_title', 'Meet Bizy, your AI recognition assistant')}
							</Text>
							<Text fontSize="xs" color="text.soft">
								{t('early_access.cta_body', 'Be the first to try Bizy when it comes to your plan.')}
							</Text>
							<Button
								colorScheme="secondary"
								variant="outline"
								size="sm"
								width="100%"
								mt={1}
								onClick={handleClick}
								isLoading={createEarlyAccessRequest.isLoading}
								data-testid="bizy-early-access-button"
							>
								{t('early_access.cta_button', 'Request early access')}
							</Button>
						</>
					)}
				</VStack>
			</Box>
		</Box>
	);
};
